import React, { Fragment, useState } from 'react';
import Localize from 'react-intl-universal';

import CloseIcon from '@mui/icons-material/Close';
import DirectionsIcon from '@mui/icons-material/Directions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { getGoogleMapsUrl } from '@pages/EventDetails/Util';

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingBottom: '0.3rem',
  fontWeight: '700',
  fontSize: '1.25rem',
  color: theme.palette.primary.contrastText
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  position: 'absolute',
  right: 8,
  top: 8
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  minHeight: '30rem',
  maxHeight: '50rem',
  margin: 'auto',
  overflowY: 'scroll',
  overflowX: 'hidden',
  '& .MuiDialog-paper': {
    backgroundColor: `${theme.palette.booking.background}!important`
  }
}));

const StyledDialogContent = styled(DialogContent)(() => ({
  paddingTop: 0,
  minHeight: '20rem',
  pl: { xs: '0.5rem', sm: '1.5rem' },
  pr: { xs: '0.5rem', sm: '1.5rem' }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none !important',
  fontSize: '14px !important',
  color: theme.palette.common.white
}));

const StyledBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: '8px',
  padding: '12px',
  marginBottom: '8px'
}));

const DirectionButton = ({ mapsUrl }) => {
  if (!mapsUrl) {
    return null;
  }

  return (
    <StyledButton
      variant='contained'
      startIcon={<DirectionsIcon />}
      onClick={() => window.open(mapsUrl, '_blank')}
    >
      {Localize.get('EventDetails.directions')}
    </StyledButton>
  );
};

const VenueItem = ({ venue }) => {
  const mapsUrl = getGoogleMapsUrl(venue);
  const { name, street, streetNumber, city, country, postCode } = venue;

  return (
    <StyledBox>
      <Grid
        container
        alignItems='center'
        spacing={1}
        justifyContent={{ xs: 'center', sm: 'flex-start' }}
        textAlign={{ xs: 'center', sm: 'left' }}
      >
        <Grid item xs={12} sm>
          <Tooltip title={name ?? ''} arrow>
            <Typography
              fontWeight='bold'
              sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-word'
              }}
            >
              {name ?? ''}
            </Typography>
          </Tooltip>
          <Typography>{[street, streetNumber].filter(Boolean).join(' ')}</Typography>
          <Typography>{[city, country].filter(Boolean).join(', ')}</Typography>
          <Typography>{postCode ?? ''}</Typography>
        </Grid>
        <Grid item xs={12} sm='auto'>
          <DirectionButton mapsUrl={mapsUrl} />
        </Grid>
      </Grid>
    </StyledBox>
  );
};

const ShowDirectionDialog = ({ venues = [] }) => {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const hasOneVenue = venues?.length === 1;
  const mapsUrl = hasOneVenue ? getGoogleMapsUrl(venues[0]) : null;

  if (!venues?.length) {
    return null;
  }

  return (
    <Fragment>
      {hasOneVenue ? (
        <DirectionButton mapsUrl={mapsUrl} />
      ) : (
        <StyledButton
          sx={(theme) => ({ color: theme.palette.primary.main })}
          onClick={handleClickOpen}
        >
          {Localize.get('EventDetails.showDirections')}
        </StyledButton>
      )}

      <StyledDialog
        open={open}
        onClose={handleClose}
        aria-labelledby='direction-dialog-title'
        aria-describedby='direction-dialog-description'
        fullWidth
        maxWidth='sm'
      >
        <StyledDialogTitle id='direction-dialog-title'>
          {Localize.get('EventDetails.showDirections')}
        </StyledDialogTitle>

        <StyledIconButton aria-label='close' onClick={handleClose}>
          <CloseIcon />
        </StyledIconButton>

        <StyledDialogContent>
          {venues?.map((venue, index) => (
            <VenueItem key={venue?.id ?? index} venue={venue} />
          ))}
        </StyledDialogContent>

        <DialogActions>
          <StyledButton onClick={handleClose}>{Localize.get('App.close')}</StyledButton>
        </DialogActions>
      </StyledDialog>
    </Fragment>
  );
};

export default ShowDirectionDialog;
